import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { navigate } from '@/providers';
import { AppRoutes } from '@/constants';
import { onChangeEmailValue, onSetEmailValue } from '@/utils';

import { Alert, handleServerValidationErrors } from '@/lib/ui';
import { Button, GlassContainer, Input, Loading, MultiSelect } from '@/components/common';

import { ROLE_OPTIONS } from '@/features/user-management/constants';

import { IUserCreation } from '@/features/user-management/user-management-creation/interfaces';
import {
  USER_CREATION_FORM_FIELDS,
  USER_CREATION_SCHEMA,
} from '@/features/user-management/user-management-creation/constants';
import { useUserCreationMutation } from '@/features/user-management/user-management-creation/hooks';

const UserManagementCreation = () => {
  const mutation = useUserCreationMutation();
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<IUserCreation>({
    resolver: yupResolver(USER_CREATION_SCHEMA),
  });

  const onSubmit = async (data: IUserCreation) => {
    await mutation.mutateAsync(data, {
      onSuccess: () => {
        navigate(AppRoutes.USER_MANAGEMENT);
        Alert.success(`User has been created and an invitation email has been sent`);
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
    navigate(AppRoutes.USER_MANAGEMENT);
  };
  return (
    <div>
      <div className="pb-3 text-display font-bold">Add User</div>
      <div className="py-3">
        <GlassContainer className="p-6 flex flex-col gap-4">
          <Input
            label="User Name"
            required
            placeholder="Enter"
            error={errors[USER_CREATION_FORM_FIELDS.fullName]?.message}
            {...register(USER_CREATION_FORM_FIELDS.fullName)}
          />
          <Input
            label="Email"
            required
            placeholder="Enter"
            error={errors[USER_CREATION_FORM_FIELDS.email]?.message}
            {...register(USER_CREATION_FORM_FIELDS.email, {
              setValueAs: onSetEmailValue,
              onChange: onChangeEmailValue,
            })}
          />
          <Controller
            name={USER_CREATION_FORM_FIELDS.roles}
            control={control}
            render={({ field }) => (
              <MultiSelect
                label="Role"
                required
                placeholder="Select"
                options={ROLE_OPTIONS}
                maxCount={-1}
                onValueChange={field.onChange}
                error={errors[USER_CREATION_FORM_FIELDS.roles]?.message}
              />
            )}
          />
        </GlassContainer>
      </div>
      <div className="flex justify-end gap-3">
        <Button
          className="min-w-[104px]"
          buttonColor="neutral"
          buttonStyle="outline"
          borderRadius="soft"
          size="lg"
          onClick={() => {
            navigate(AppRoutes.USER_MANAGEMENT);
          }}
        >
          Cancel
        </Button>
        <Button
          className="min-w-[104px]"
          buttonColor="primary"
          buttonStyle="filled"
          borderRadius="soft"
          size="lg"
          onClick={handleSubmit(onSubmit)}
        >
          Confirm
        </Button>
      </div>
      {mutation.isPending && <Loading fullScreen />}
    </div>
  );
};

export default UserManagementCreation;
